import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import NavigationButtons from '@/components/NavigationButtons';
import RelatedLinks from '@/components/RelatedLinks';

const Blog = () => {
  const navigate = useNavigate();
  
  const blogPosts = [
    {
      id: 12,
      title: "DeepSeek-R2: China's Bold Answer to the AI Race – What You Need to Know",
      date: "April 27, 2025",
      excerpt: "Discover how DeepSeek-R2, the next-gen AI model from China, challenges Silicon Valley with multilingual reasoning, coding skills, and multimodal capabilities.",
      category: "AI Technology",
      slug: "deepseek-r2-ai-model-launch-2025"
    },
    {
      id: 11,
      title: "Llama 4 vs DeepSeek AI: Comprehensive Comparison of Leading AI Models",
      date: "April 10, 2025",
      excerpt: "Dive into a detailed comparison of Meta's Llama 4 and DeepSeek AI across benchmarks, specialized capabilities, and use cases to determine which model is best for your needs.",
      category: "AI Technology",
      slug: "llama-4-vs-deepseek"
    },
    {
      id: 10,
      title: "What is DeepSeek AI? Unveiling China's Groundbreaking Open-Source Revolution",
      date: "April 3, 2025",
      excerpt: "Discover how DeepSeek AI is challenging Silicon Valley's AI dominance with cost-effective, open-source models that deliver state-of-the-art performance.",
      category: "AI Technology",
      slug: "what-is-deepseek-ai"
    },
    {
      id: 9,
      title: "DeepSeek V3.1: The New Frontier in Artificial Intelligence",
      date: "March 25, 2025",
      excerpt: "Introducing DeepSeek V3.1, our most powerful AI model with unprecedented reasoning capabilities, extended context window, and superior multilingual support.",
      category: "AI Technology",
      slug: "deepseek-v31"
    },
    {
      id: 8,
      title: "What is Deep Seek?",
      date: "March 22, 2025",
      excerpt: "Discover Deep Seek, an advanced search technology that goes beyond basic keyword searches to deliver precise, context-aware results for researchers and businesses.",
      category: "Technology",
      slug: "what-is-deep-seek"
    },
    {
      id: 7,
      title: "What Are the Potential Challenges Deep Seek Might Face with the Early Release of R2",
      date: "March 22, 2025",
      excerpt: "Explore the challenges Deep Seek faces with its early R2 AI model release, including technical hurdles, user experience concerns, and competitive pressures.",
      category: "AI Technology",
      slug: "deepseek-r2-challenges"
    },
    {
      id: 6,
      title: "What is Deep Learning AI",
      date: "March 22, 2025",
      excerpt: "Learn about deep learning AI, a subset of machine learning that uses neural networks to analyze data and make predictions, powering innovations in image recognition, NLP and more.",
      category: "AI Technology",
      slug: "what-is-deep-learning-ai"
    },
    {
      id: 0,
      title: "Vibe Coding: The Future of Software Development",
      date: "March 11, 2025",
      excerpt: "Discover how Vibe Coding is revolutionizing software development by leveraging AI to generate code from natural language prompts.",
      category: "AI Technology",
      slug: "vibe-coding"
    },
    {
      id: 1,
      title: "The Evolution of AI Chat Technology",
      date: "March 11, 2025",
      excerpt: "Explore the latest advancements in AI chat technology, from natural language processing to context awareness. Learn how AI chatbots are transforming communication.",
      category: "AI Technology",
      slug: "ai-chat"
    },
    {
      id: 2,
      title: "Deep Seek Stock - Market Intelligence",
      date: "March 11, 2025",
      excerpt: "Access comprehensive market data and investment insights about Deep Seek's stock performance and future outlook.",
      category: "Finance",
      slug: "deepseek-stock"
    },
    {
      id: 3,
      title: "Deep Seek R1 - Advanced AI Model",
      date: "March 11, 2025",
      excerpt: "Explore Deep Seek R1, the state-of-the-art open source AI model designed to push the boundaries of artificial intelligence.",
      category: "AI Technology",
      slug: "deepseek-r1"
    },
    {
      id: 4,
      title: "The Future of AI in Enterprise",
      date: "March 11, 2025",
      excerpt: "Exploring how artificial intelligence is reshaping enterprise operations and decision-making processes.",
      category: "AI Trends",
      slug: "future-of-ai-in-enterprise"
    },
    {
      id: 5,
      title: "Understanding Deep Learning Models",
      date: "March 11, 2025",
      excerpt: "A comprehensive guide to modern deep learning architectures and their applications.",
      category: "Technical",
      slug: "understanding-deep-learning-models"
    }
  ];

  const handleReadMore = (slug: string) => {
    console.log('Navigating to article:', slug);
    if (slug === 'deepseek-stock') {
      navigate('/deepseek-stock');
    } else if (slug === 'deepseek-r1') {
      navigate('/deepseek-r1');
    } else if (slug === 'what-is-deep-learning-ai') {
      navigate('/blog/what-is-deep-learning-ai');
    } else if (slug === 'vibe-coding') {
      navigate('/blog/vibe-coding');
    } else if (slug === 'deepseek-r2-challenges') {
      navigate('/blog/deepseek-r2-challenges');
    } else if (slug === 'what-is-deep-seek') {
      navigate('/blog/what-is-deep-seek');
    } else if (slug === 'deepseek-v31') {
      navigate('/blog/deepseek-v31');
    } else if (slug === 'what-is-deepseek-ai') {
      navigate('/blog/what-is-deepseek-ai');
    } else if (slug === 'llama-4-vs-deepseek') {
      navigate('/blog/llama-4-vs-deepseek');
    } else if (slug === 'deepseek-r2-ai-model-launch-2025') {
      navigate('/blog/deepseek-r2-ai-model-launch-2025');
    } else {
      navigate(`/blog/${slug}`);
    }
  };

  return (
    <>
      <Helmet>
        <title>Deep Seek AI Blog | Latest Insights on AI Technology & Innovation</title>
        <meta name="description" content="Explore top AI articles covering deep learning, AI models, and innovative search technologies that are transforming industries." />
        <meta name="keywords" content="AI blog, artificial intelligence insights, machine learning trends, deep learning technology, AI innovation, Deep Seek AI blog, vibe coding, DeepSeek V3.1" />
        <link rel="canonical" href="https://deepseek.ai/blog" />
        
        <meta property="og:title" content="Deep Seek AI Blog | Expert Insights on AI Technology" />
        <meta property="og:description" content="Explore cutting-edge insights on artificial intelligence, machine learning, and deep learning from Deep Seek AI experts." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://deepseek.ai/blog" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Deep Seek AI Blog | AI Technology Insights" />
        <meta name="twitter:description" content="Expert insights on artificial intelligence and machine learning from Deep Seek AI." />
      </Helmet>

      <div className="min-h-screen pt-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Latest Articles on AI & Machine Learning</h1>

          <NavigationButtons />

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {blogPosts.map((post) => (
              <Card key={post.id} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-sm text-gray-500">{post.date}</span>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                      {post.category}
                    </span>
                  </div>
                  <CardTitle className="text-2xl mb-2">{post.title}</CardTitle>
                  <CardDescription>{post.excerpt}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    variant="link" 
                    className="text-blue-600 hover:text-blue-800 p-0"
                    onClick={() => handleReadMore(post.slug)}
                  >
                    Read more →
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Recommended Resources</h2>
            <RelatedLinks />
          </div>

          <div className="mt-16 bg-blue-50 p-8 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">About Our Blog</h2>
            <p className="text-gray-700 mb-4">
              The Deep Seek AI blog is dedicated to exploring the latest advancements in artificial intelligence, machine learning, and deep learning technologies. 
              Our team of experts regularly publishes in-depth articles, tutorials, and insights to help you stay informed about the rapidly evolving world of AI.
            </p>
            <p className="text-gray-700">
              Whether you're a researcher, developer, business leader, or simply curious about AI, our blog offers valuable content to enhance your understanding and application of these transformative technologies.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Blog;
